import { Fragment } from 'react/jsx-runtime'
import { Address, ObjectProperty, ObjectPropertyValue } from '../../shared/types'
import { ObjectTypes } from '../api/object'
import { BrandFormatter, BrandLinkFormatter } from '../components/brand-formatter'
import { ButtonLink } from '../components/button'
import { ClientFormatter } from '../components/client-formatter'
import { CloudinaryImage } from '../components/cloudinary-image'
import { CurrencyFormatter } from '../components/currency-formatter'
import { DateFormatter } from '../components/date-formatter'
import { Due } from '../components/due-formatter'
import { LocationFormatter } from '../components/location-formatter'
import { ProposalVehicleFormatter } from '../components/proposal-vehicle-formatter'
import { SourceFormatter } from '../components/source-formatter'
import { UserFormatter } from '../components/user-formatter'
import { VehicleFormatter } from '../components/vehicle-formatter'
import { TableColumn } from '../layouts/types'
import { STATUS, STATUS_STYLES } from '../reservations/const'

export type PropertyFormatter = Exclude<keyof typeof FORMATTER, 'reference'>

const mightBeTableColumn = (column: ObjectProperty | TableColumn): column is TableColumn =>
	'formatter' in column

export const getFormatter = (column: ObjectProperty | TableColumn) => {
	const { type, referenceTo } = column
	if (type === 'reference' && hasReferenceFormatter(referenceTo)) {
		return FORMATTER.reference[referenceTo]
	}
	if (mightBeTableColumn(column) && hasFormatter(column.formatter)) {
		return FORMATTER[column.formatter]
	}
	if (hasFormatter(type)) {
		return FORMATTER[type]
	}
	return (i: ObjectPropertyValue) => i
}

const hasFormatter = (type: string | undefined): type is PropertyFormatter =>
	Boolean(type && type in FORMATTER)

const hasReferenceFormatter = (
	referenceTo?: string,
): referenceTo is keyof typeof FORMATTER.reference =>
	Boolean(referenceTo && referenceTo in FORMATTER.reference)

const formatAddressLine = (array: Array<string | undefined>) =>
	array.filter(Boolean).map((line, index) => (
		<Fragment key={index}>
			{line}
			<br />
		</Fragment>
	))

const shouldShowProposalVehicleInternalName = (data?: ObjectTypes[keyof ObjectTypes]) =>
	Boolean(data && typeof data === 'object' && 'vehicle_id' in data)

export const FORMATTER = {
	address: (
		_: ObjectPropertyValue,
		__?: ObjectProperty,
		data?: ObjectTypes[keyof ObjectTypes],
	) => {
		if (!data) return null
		const address = data as unknown as Address
		return (
			<>
				{formatAddressLine([address.address_1, address.address_2])}
				{formatAddressLine([
					`${address.postal_code ?? ''} ${address.city}`,
					address.state,
					address.country,
				])}
			</>
		)
	},
	boolean: (value: ObjectPropertyValue) => (value && value !== '0' ? 'Ano' : 'Ne'),
	brandLink: (
		_: ObjectPropertyValue,
		__?: ObjectProperty,
		data?: ObjectTypes[keyof ObjectTypes],
	) => <BrandLinkFormatter id={data?.brand as string} suffix={data?.public_id as string} />,
	cloudinary: (value: ObjectPropertyValue) => <CloudinaryImage id={value as string} />,
	color: (color: ObjectPropertyValue) => {
		if (!color) return null
		return <div className="rounded-full w-4 h-4" style={{ background: color as string }} />
	},
	currency: (value: ObjectPropertyValue) => <CurrencyFormatter value={value as string} />,
	datetime: (date: ObjectPropertyValue) => <DateFormatter date={date as string} />,
	dateRange: (
		_: ObjectPropertyValue,
		__?: ObjectProperty,
		data?: ObjectTypes[keyof ObjectTypes],
	) => (
		<>
			<DateFormatter date={data?.date_from as string} format="d.M. HH:mm" />
			{' - '}
			<DateFormatter date={data?.date_to as string} format="d.M. HH:mm" />
		</>
	),
	derived: (value: ObjectPropertyValue) => <span>{value}</span>,
	due: (_: ObjectPropertyValue, __?: ObjectProperty, data?: ObjectTypes[keyof ObjectTypes]) => (
		<Due id={data?.id as string} />
	),
	html: (value: ObjectPropertyValue) => (
		<div className="prose" dangerouslySetInnerHTML={{ __html: value as string }} />
	),
	multi: (value: ObjectPropertyValue, prop?: ObjectProperty) => {
		if (!value) return <em className="text-gray-400">nevyplněno</em>
		const label = (value as Array<string>).map(
			val => prop?.options?.find(option => option.id === val)?.name ?? value,
		)
		return <span>{label.join(', ')}</span>
	},
	single: (value: ObjectPropertyValue, prop?: ObjectProperty) => {
		if (!value) return <em className="text-gray-400">nevyplněno</em>
		const label = prop?.options?.find(option => option.id === value)?.name ?? value
		return <span>{label}</span>
	},
	source: (value: ObjectPropertyValue) => <SourceFormatter source={value as string} />,
	status: (value: ObjectPropertyValue) => {
		const status = STATUS[value as keyof typeof STATUS]
		return (
			<span
				className={`px-3 py-1 rounded-xl text-xs font-semibold ${STATUS_STYLES[value as keyof typeof STATUS_STYLES]}`}
			>
				{status}
			</span>
		)
	},
	reference: {
		brand: (id: ObjectPropertyValue) => <BrandFormatter id={id as string} />,
		client: (id: ObjectPropertyValue) => <ClientFormatter id={id as string} />,
		location: (id: ObjectPropertyValue) => <LocationFormatter id={id as string} />,
		reservation: (id: ObjectPropertyValue) => (
			<ButtonLink to={`/svc/reservation/${id}`} variant="TEXT">
				{id}
			</ButtonLink>
		),
		proposal: (id: ObjectPropertyValue) => (
			<ButtonLink to={`/svc/proposal/${id}`} variant="TEXT" size="TEXT">
				{id}
			</ButtonLink>
		),
		proposal_vehicle: (
			id: ObjectPropertyValue,
			__?: ObjectProperty,
			data?: ObjectTypes[keyof ObjectTypes],
			object?: keyof ObjectTypes,
		) => (
			<ProposalVehicleFormatter
				id={id as string}
				showInternalName={!object && shouldShowProposalVehicleInternalName(data)}
			/>
		),
		vehicle: (id: ObjectPropertyValue) => <VehicleFormatter id={id as string} />,
		user: (id: ObjectPropertyValue) => <UserFormatter id={id as string} />,
	},
	vyfakturuj: (
		id: ObjectPropertyValue,
		_?: ObjectProperty,
		data?: ObjectTypes[keyof ObjectTypes],
		object?: keyof ObjectTypes,
	) =>
		id && data && object ? (
			<a
				href={`/api/0/${object}/${data.id}/actions/vyfakturuj/redirect`}
				target="_blank"
				rel="noreferrer noopener"
				className="text-rose-700 hover:underline"
			>
				{id} ⤴
			</a>
		) : null,
} as const
